import { forwardRef, type ChangeEvent, type Ref } from "react";

import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import FlexWrapper, { AlignItems, FlexDirection } from "../containers/FlexWrapper";
import Spacing from "../containers/Spacing";
import HelpIcon from "../icons/HelpIcon";
import Icon, { IconVariant } from "../icons/Icon";
import RequiredMarker from "../text/RequiredMarker";
import Text, { TextSize, TextVariant } from "../text/Text";
import Tooltip, { TooltipPosition } from "../tooltip/Tooltip";

export interface InputLabelProps {
  label: string;
  labelTooltip?: string | React.ReactNode;
  isRequired?: boolean;
}

export const InputLabel = ({ label, labelTooltip, isRequired }: InputLabelProps) => {
  return (
    <FlexWrapper alignItems={AlignItems.CENTER} gap={1}>
      <Text variant={TextVariant.SECONDARY}>{label}</Text>
      {isRequired && <RequiredMarker />}
      {labelTooltip && (
        <Spacing left={4}>
          <Tooltip body={labelTooltip} position={TooltipPosition.RIGHT}>
            <HelpIcon />
          </Tooltip>
        </Spacing>
      )}
    </FlexWrapper>
  );
};

export enum InputSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export const StyledInput = withTheme(styled.input<
  PropsWithTheme<{
    $size: InputSize;
    $width?: number | string;
    $fillWidth?: boolean;
    $isError?: boolean;
    $hasLeadingIcon?: boolean;
    $hasTrailingAction?: boolean;
    $isMonospace?: boolean;
  }>
>`
  outline: none;
  height: ${({ $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => "24px")
      .with(InputSize.MEDIUM, () => "28px")
      .with(InputSize.LARGE, () => "32px")
      .exhaustive();
  }};
  width: ${({ $fillWidth, $width }) =>
    $fillWidth ? "100%" : $width ? getCssSizeValue($width) : "auto"};

  background-color: ${({ theme }) => theme.color.background.primary};
  border: 0.5px solid
    ${({ theme, $isError }) => {
      if ($isError) {
        return theme.color.border.error;
      }

      return theme.color.border.primary;
    }};
  border-radius: 5px;
  color: ${({ theme }) => theme.color.text.primary};

  font-size: ${({ theme, $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => theme.font.sans.size.body_sm)
      .with(InputSize.MEDIUM, () => theme.font.sans.size.body_md)
      .with(InputSize.LARGE, () => theme.font.sans.size.body_lg)
      .exhaustive();
  }};
  line-height: ${({ theme, $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => theme.font.sans.height.body_sm)
      .with(InputSize.MEDIUM, () => theme.font.sans.height.body_md)
      .with(InputSize.LARGE, () => theme.font.sans.height.body_lg)
      .exhaustive();
  }};
  font-weight: ${({ theme, $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => theme.font.sans.weight.regular)
      .with(InputSize.MEDIUM, () => theme.font.sans.weight.regular)
      .with(InputSize.LARGE, () => theme.font.sans.weight.regular)
      .exhaustive();
  }};
  font-family: ${({ theme, $isMonospace }) =>
    $isMonospace ? theme.font.mono.family : theme.font.sans.family};

  padding: ${({ $size, $hasLeadingIcon, $hasTrailingAction }) => {
    const leftPadding = match($size)
      .with(InputSize.SMALL, () => ($hasLeadingIcon ? "24px" : "6px"))
      .with(InputSize.MEDIUM, () => ($hasLeadingIcon ? "28px" : "8px"))
      .with(InputSize.LARGE, () => ($hasLeadingIcon ? "32px" : "12px"))
      .exhaustive();

    const rightPadding = match($size)
      .with(InputSize.SMALL, () => ($hasTrailingAction ? "28px" : "6px"))
      .with(InputSize.MEDIUM, () => ($hasTrailingAction ? "32px" : "8px"))
      .with(InputSize.LARGE, () => ($hasTrailingAction ? "38px" : "12px"))
      .exhaustive();

    return `0 ${rightPadding} 0 ${leftPadding}`;
  }};

  transition: border-color 0.075s ease-in-out;

  &:hover:not(:disabled, :focus) {
    border-color: ${({ theme }) => theme.color.border.primary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.color.border.selected};
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.background.disabled};
    border-color: ${({ theme }) => theme.color.border.disabled};
    cursor: not-allowed;
  }
`);

const InputContainer = styled.div<{
  $width?: number | string;
  $fillWidth?: boolean;
}>`
  position: relative;
  width: ${({ $fillWidth, $width }) =>
    $fillWidth ? "100%" : $width ? getCssSizeValue($width) : "auto"};
`;

const LeadingIconWrapper = withTheme(styled.div<
  PropsWithTheme<{
    $size: InputSize;
  }>
>`
  position: absolute;
  left: ${({ $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => "6px")
      .with(InputSize.MEDIUM, () => "8px")
      .with(InputSize.LARGE, () => "12px")
      .exhaustive();
  }};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`);

const TrailingActionButton = styled.button<{
  $size: InputSize;
  $isDisabled?: boolean;
}>`
  position: absolute;
  right: ${({ $size }) => {
    return match($size)
      .with(InputSize.SMALL, () => "6px")
      .with(InputSize.MEDIUM, () => "8px")
      .with(InputSize.LARGE, () => "12px")
      .exhaustive();
  }};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
`;

export type InputValue = string | number | readonly string[] | undefined;

const INPUT_SIZE_TO_ICON_SIZE_MAP: Record<InputSize, number> = {
  [InputSize.SMALL]: 12,
  [InputSize.MEDIUM]: 14,
  [InputSize.LARGE]: 14,
};

export interface InputProps<T> {
  parse: (value: T) => T;
  onChange: (value: T) => void;
  size?: InputSize;
  type: HTMLInputElement["type"];
  value?: T;
  placeholder?: string;
  leading?: {
    icon: PhosphorIcon;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  };
  trailing?: {
    icon: PhosphorIcon;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isDisabled?: boolean;
  };
  label?: string;
  labelTooltip?: string | React.ReactNode;
  error?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  min?: number;
  max?: number;
  step?: number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  width?: number | string;
  fillWidth?: boolean;
  isMonospace?: boolean;
}

// eslint-disable-next-line react/display-name
const Input = forwardRef(
  <T extends InputValue>(
    {
      parse,
      onChange,
      size = InputSize.MEDIUM,
      type,
      value,
      label,
      labelTooltip,
      error,
      placeholder,
      leading,
      trailing,
      isRequired,
      isDisabled,
      autoFocus,
      min,
      max,
      step,
      onFocus,
      onBlur,
      onKeyDown,
      width,
      fillWidth,
      isMonospace,
    }: InputProps<T>,
    ref: Ref<HTMLInputElement>,
  ) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parse(e.target.value as T);
      onChange(parsedValue);
    };

    const hasLeading = !!leading?.icon;
    const hasTrailing = !!trailing?.icon;

    return (
      <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth={fillWidth}>
        {label && <InputLabel label={label} labelTooltip={labelTooltip} isRequired={isRequired} />}
        <InputContainer $width={width} $fillWidth={fillWidth}>
          {hasLeading && (
            <LeadingIconWrapper $size={size}>
              <Icon
                component={leading.icon}
                variant={isDisabled ? IconVariant.DISABLED : IconVariant.SECONDARY}
                size={INPUT_SIZE_TO_ICON_SIZE_MAP[size]}
              />
            </LeadingIconWrapper>
          )}
          <StyledInput
            $size={size}
            $width={width}
            $fillWidth={fillWidth || hasLeading || hasTrailing}
            $isError={!!error}
            $hasLeadingIcon={hasLeading}
            $hasTrailingAction={hasTrailing}
            $isMonospace={isMonospace}
            ref={ref}
            type={type}
            placeholder={placeholder}
            value={value?.toString() ?? ""}
            required={isRequired}
            disabled={isDisabled}
            onChange={handleChange}
            autoComplete="off"
            autoCorrect="off"
            min={min}
            max={max}
            step={step}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
          />
          {hasTrailing && (
            <TrailingActionButton
              onClick={trailing.onClick}
              $size={size}
              $isDisabled={isDisabled || trailing.isDisabled}
              disabled={isDisabled || trailing.isDisabled}
            >
              <Icon
                component={trailing.icon}
                variant={IconVariant.SECONDARY}
                size={INPUT_SIZE_TO_ICON_SIZE_MAP[size]}
              />
            </TrailingActionButton>
          )}
        </InputContainer>
        {error && (
          <Text size={TextSize.BODY_SM} variant={TextVariant.ERROR}>
            {error}
          </Text>
        )}
      </FlexWrapper>
    );
  },
) as <T extends InputValue>(
  props: InputProps<T> & { ref?: Ref<HTMLInputElement> },
) => React.ReactElement;

const InputWithDisplayName = Input as unknown as typeof Input & {
  displayName: string;
};
InputWithDisplayName.displayName = "Input";

export default Input;
